import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const CompoundInterestTool = ({
  retirementDate,
  onClose,
  rate,
  principal,
  setPrincipal,
  setRate,
}) => {
  const [years, setYears] = useState(0);

  const [endingBalance, setEndingBalance] = useState(0);
  const [compoundInterest, setCompoundInterest] = useState(0);

  // ✅ Calculate YEARS (whole years only)
  useEffect(() => {
    if (!retirementDate) return;

    const today = new Date();
    const retirement = new Date(retirementDate);

    let diff = retirement.getFullYear() - today.getFullYear();

    if (
      retirement.getMonth() < today.getMonth() ||
      (retirement.getMonth() === today.getMonth() &&
        retirement.getDate() < today.getDate())
    ) {
      diff--;
    }

    // setYears(Math.max(diff, 0));
  }, [retirementDate]);

  // ✅ Calculate compound interest with yearly contributions
  useEffect(() => {
    const r = rate / 100;

    let balance = 0;
    let totalContributions = 0;

    for (let i = 0; i < years; i++) {
      // add yearly contribution
      balance += principal;
      totalContributions += principal;

      // grow for 1 year
      balance *= 1 + r;
    }

    const roundedBalance = Math.round(balance);

    setEndingBalance(roundedBalance);
    setCompoundInterest(Math.round(roundedBalance - totalContributions));
  }, [principal, rate, years]);

  // ✅ Generate chart data
  const generateData = () => {
    const data = [];
    const r = rate / 100;

    for (let i = 0; i <= years; i++) {
      const value = Math.round(principal * Math.pow(1 + r / 12, 12 * i));
      data.push({ year: i, value });
    }

    return data;
  };

  const data = generateData();

  const formatCurrency = (num) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(num);

  return (
    <>
      {/* Backdrop */}
      <div className="modal-backdrop fade show" onClick={onClose}></div>

      {/* ✅ Bootstrap Modal */}
      <div className="modal fade show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content p-4">
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title mx-auto font-weight-bold">
                Compound Interest Calculator
              </h5>
              <button
                className="btn-close"
                onClick={onClose}
                style={{ position: "absolute", top: "10px", right: "10px" }}
              ></button>
            </div>

            {/* Body */}
            <div className="modal-body">
              {/* Inputs */}
              <div className="row g-3 mb-4">
                <div className="col-md-4 text-center">
                  <label className="form-label font-weight-bold">
                    Annual Contribution
                  </label>
                  <input
                    type="number"
                    className="form-control text-center"
                    value={principal}
                    onChange={(e) => setPrincipal(Number(e.target.value))}
                  />
                </div>

                <div className="col-md-4 text-center">
                  <label className="form-label font-weight-bold">
                    Interest Rate (%)
                  </label>
                  <input
                    type="number"
                    className="form-control text-center"
                    value={rate}
                    onChange={(e) => setRate(Number(e.target.value))}
                  />
                </div>

                <div className="col-md-4 text-center">
                  <label className="form-label font-weight-bold">Years</label>
                  <input
                    type="number"
                    className="form-control text-center"
                    value={years}
                    onChange={(e) => setYears(Number(e.target.value))}
                  />
                </div>
              </div>

              {/* Chart */}
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data}>
                    <XAxis dataKey="year" />

                    <YAxis
                      tickFormatter={(value) => `$${value.toLocaleString()}`}
                    />

                    <Tooltip
                      formatter={(value) => `$${value.toLocaleString()}`}
                    />

                    <Line type="monotone" dataKey="value" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Results */}
              <div className="row text-center mt-4">
                <div className="col-md-6">
                  <div className="p-3 border rounded">
                    <div className="text-muted">Compound Interest</div>
                    <h5 style={{ fontWeight: "bold" }}>
                      {formatCurrency(compoundInterest)}
                    </h5>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 border rounded">
                    <div className="text-muted">Ending Balance</div>
                    <h5 style={{ fontWeight: "bold" }}>
                      {formatCurrency(endingBalance)}
                    </h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompoundInterestTool;
